import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Download, Phone } from "lucide-react";

import { cn } from "@/lib/utils";
import { PHONE_DISPLAY, PHONE_HREF } from "@/content/site";
import { trackCTA } from "@/lib/tracking";
import markAsset from "@/assets/drsully-mark.png.asset.json";

const NAV = [
  { label: "About", href: "/about" },
  { label: "Speaking", href: "/speaking" },
  { label: "Consulting", href: "/inquire?lane=consulting" },
  { label: "Make Change Scan", href: "/scan" },
  { label: "CERC", href: "/framework#cerc" },
  { label: "Resources", href: "/ebooks" },
  { label: "Insights", href: "/blog" },
  { label: "Contact", href: "/inquire" },
];

const linkBase =
  "relative whitespace-nowrap text-[11px] 2xl:text-xs uppercase tracking-[0.1em] 2xl:tracking-[0.2em] transition-colors outline-none focus-visible:ring-2 focus-visible:ring-gold/70 focus-visible:ring-offset-2 focus-visible:ring-offset-navy-deep rounded-sm";
const linkInactive = "text-silver hover:text-white";
const linkActive = "text-white";

export const SiteNav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const onAdvocacy = pathname === "/advocacy" || pathname.startsWith("/advocacy/");


  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  // Lock body scroll while the mobile menu is open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  // Close menu on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      {/* Skip link for keyboard users */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[60] focus:bg-gold focus:text-navy-deep focus:px-4 focus:py-2 focus:font-display focus:uppercase focus:tracking-[0.2em] focus:text-xs focus:rounded"
      >
        Skip to content
      </a>

      <header
        role="banner"
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
          scrolled || open
            ? "bg-navy-deep/90 backdrop-blur-md border-b border-border"
            : "bg-transparent",
        )}
      >
        <div className="container flex items-center justify-between gap-4 lg:gap-8 py-4">
          <Link
            to="/"
            aria-label="DrSullySpeaks — Home"
            className="flex items-center gap-2.5 group rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-gold/70"
          >
            <span className="relative inline-flex h-10 w-10 md:h-11 md:w-11 rounded-full overflow-hidden ring-1 ring-gold/70 bg-white shadow-[0_4px_14px_-4px_hsl(var(--gold)/0.55)]">
              <img src={markAsset.url} alt="" className="h-full w-full object-contain p-0.5" />
            </span>
            <span className="flex items-baseline">
              <span className="font-display font-bold uppercase tracking-[0.18em] text-white text-sm md:text-base">
                DrSully
              </span>
              <span className="font-display font-bold uppercase tracking-[0.18em] text-orange text-sm md:text-base">
                Speaks
              </span>
            </span>
          </Link>

          <nav aria-label="Primary" className="hidden md:flex items-center gap-2 lg:gap-3 2xl:gap-6 min-w-0">
            {NAV.map((n) => (
              <NavLink
                key={n.label}
                to={n.href}
                end={n.href === "/"}
                className={({ isActive }) =>
                  cn(linkBase, "py-1", n.href === "/scan" && "hidden xl:inline-block", isActive ? linkActive : linkInactive)
                }
              >
                {({ isActive }) => (
                  <>
                    {n.label}
                    <span
                      aria-hidden="true"
                      className={cn(
                        "absolute left-0 -bottom-1 h-px bg-gold transition-all duration-300",
                        isActive ? "w-full" : "w-0 group-hover:w-full",
                      )}
                    />
                  </>
                )}
              </NavLink>
            ))}
            <Link
              to="/app"
              onClick={() => trackCTA("app_download", { surface: "nav_desktop" })}
              aria-label="Download the MAKE CHANGE NOW app"
              className="inline-flex shrink-0 items-center gap-1 rounded-md border border-white/70 px-2 py-2 text-[10px] font-display uppercase tracking-[0.18em] text-white whitespace-nowrap transition-colors hover:bg-white/10 outline-none focus-visible:ring-2 focus-visible:ring-white/70"
            >
              <Download aria-hidden="true" className="h-3.5 w-3.5" />
              <span className="hidden 2xl:inline">Get the App</span>
              <span className="hidden xl:inline 2xl:hidden">App</span>
            </Link>
            {/* Unified conversion cluster: advocacy entry point + primary scan CTA */}
            <div className="group/cluster ml-1 flex items-stretch rounded-lg overflow-hidden ring-1 ring-white/10 shadow-[var(--shadow-cta)]">
              <Link
                to="/advocacy"
                onClick={() => trackCTA("advocacy_cta", { surface: "nav_desktop" })}
                aria-current={onAdvocacy ? "page" : undefined}
                className={cn(
                  "relative w-[86px] shrink-0 flex flex-col items-center justify-center gap-1 px-2 py-2.5 text-center bg-[image:var(--gradient-advocacy)] text-white transition-all hover:brightness-110 active:scale-[0.98] outline-none focus-visible:ring-2 focus-visible:ring-advocacy-glow focus-visible:ring-inset",
                  onAdvocacy && "ring-2 ring-inset ring-gold/70 brightness-110",
                )}
                aria-label={
                  onAdvocacy
                    ? "Special Needs Advocacy — Full-Scope Advocacy Partner (current page)"
                    : "Special Needs Advocacy — Full-Scope Advocacy Partner"
                }
              >
                <span aria-hidden="true" className="text-sm leading-none opacity-90">✦</span>
                <span className="font-display uppercase text-[9px] leading-[1.35] tracking-[0.16em]">
                  Special Needs
                  <br />
                  Advocacy
                </span>
                <span className="mt-0.5 border-t border-white/25 pt-1 font-display uppercase text-[10px] tracking-[0.22em]">
                  FSAP
                </span>
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 bg-white/10 opacity-0 transition-opacity duration-500 group-hover/cluster:opacity-100"
                />
              </Link>

              <Link
                to="/scan"
                onClick={() => trackCTA("scan_cta", { surface: "nav_desktop" })}
                className="relative flex flex-col justify-center gap-1.5 px-3 xl:px-4 py-2.5 bg-cta transition-all hover:brightness-110 active:scale-[0.98] outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-navy-deep/70"
              >
                <span className="font-display uppercase text-white leading-[1.1] tracking-[0.06em] drop-shadow-sm">
                  <span className="block text-[10px] tracking-[0.2em] text-white/85 whitespace-nowrap">Start Your Free</span>
                  <span className="block text-[13px] xl:text-base text-navy-deep whitespace-nowrap">Make Change Scan</span>
                </span>
                <span className="inline-flex items-center gap-2 self-start rounded-md border border-white/20 bg-navy-deep/10 px-2 py-0.5 backdrop-blur-[2px]">
                  <Phone aria-hidden="true" className="h-3.5 w-3.5 text-navy-deep" strokeWidth={2.5} />
                  <span className="font-display font-bold text-navy-deep text-xs xl:text-sm tracking-[0.08em] whitespace-nowrap">
                    {PHONE_DISPLAY}
                  </span>
                </span>
              </Link>
            </div>

          </nav>

          <button
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="mobile-nav"
            onClick={() => setOpen((s) => !s)}
            className="md:hidden text-white p-2 min-h-11 min-w-11 inline-flex items-center justify-center rounded outline-none focus-visible:ring-2 focus-visible:ring-gold/70"
          >
            <div className="w-6 flex flex-col gap-1.5" aria-hidden="true">
              <span className={cn("h-px bg-white transition-all", open && "translate-y-[6px] rotate-45")} />
              <span className={cn("h-px bg-white transition-all", open && "opacity-0")} />
              <span className={cn("h-px bg-white transition-all", open && "-translate-y-[6px] -rotate-45")} />
            </div>
          </button>
        </div>

        {/* Backdrop — tap outside to close */}
        <div
          aria-hidden="true"
          onClick={() => setOpen(false)}
          className={cn(
            "md:hidden fixed inset-x-0 top-[72px] bottom-0 bg-navy-deep/60 backdrop-blur-sm transition-opacity duration-300",
            open ? "opacity-100" : "opacity-0 pointer-events-none",
          )}
        />

        <div
          id="mobile-nav"
          // While the panel is faded out it stays in the DOM for the
          // transition, so `inert` + `aria-hidden` remove its links from
          // both the tab order and the accessibility tree.
          aria-hidden={!open || undefined}
          {...(!open ? { inert: "" as unknown as boolean } : {})}
          className={cn(
            "md:hidden relative z-10 border-t border-border bg-navy-deep/95 backdrop-blur-md max-h-[calc(100dvh-72px)] overflow-y-auto origin-top transition-all duration-300 ease-out",
            open
              ? "opacity-100 translate-y-0 scale-y-100"
              : "opacity-0 -translate-y-2 scale-y-95 pointer-events-none h-0 invisible",
          )}
        >

          <nav aria-label="Mobile" className="container py-6 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.label}
                to={n.href}
                end={n.href === "/"}
                className={({ isActive }) =>
                  cn(
                    "text-sm uppercase tracking-[0.25em] py-3 border-l-2 pl-3 transition-colors",
                    isActive
                      ? "text-white border-gold bg-white/[0.03]"
                      : "text-silver border-transparent hover:text-white hover:border-gold/40",
                  )
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/advocacy"
              onClick={() => trackCTA("advocacy_cta", { surface: "nav_mobile" })}
              aria-current={onAdvocacy ? "page" : undefined}
              aria-label="Special Needs Advocacy — Full-Scope Advocacy Partner"
              className={cn(
                "mt-4 text-white font-display uppercase tracking-[0.18em] text-xs px-5 py-4 min-h-11 rounded border bg-[image:var(--gradient-advocacy)] shadow-[var(--shadow-advocacy)] text-center flex items-center justify-center gap-2 leading-tight active:brightness-110 active:scale-[0.98] transition-all touch-manipulation outline-none focus-visible:ring-2 focus-visible:ring-advocacy-glow/70 focus-visible:ring-offset-2 focus-visible:ring-offset-navy-deep",
                onAdvocacy ? "border-gold ring-2 ring-gold/60 brightness-110" : "border-advocacy/60",
              )}
            >
              <span aria-hidden="true">✦</span>
              <span>Special Needs Advocacy · FSAP</span>
              {onAdvocacy && (
                <span className="text-[9px] tracking-[0.28em] text-white">· Current<span className="sr-only"> page</span></span>
              )}
            </Link>


            <Link
              to="/scan"
              onClick={() => trackCTA("scan_cta", { surface: "nav_mobile" })}
              className="mt-3 bg-cta text-white font-display uppercase tracking-[0.2em] text-xs px-5 py-4 shadow-cta active:brightness-110 active:scale-[0.98] text-center rounded flex flex-col items-center leading-tight transition-all touch-manipulation"
            >
              <span>Start Your Free Make Change Scan</span>
              <span className="font-display font-bold text-navy-deep text-base tracking-[0.12em] mt-1.5">{PHONE_DISPLAY}</span>
            </Link>
            <Link
              to="/app"
              onClick={() => trackCTA("app_download", { surface: "nav_mobile" })}
              className="mt-3 inline-flex items-center justify-center gap-2 rounded border border-gold/60 px-5 py-4 min-h-11 font-display uppercase tracking-[0.2em] text-xs text-gold active:scale-[0.98] transition-all touch-manipulation"
            >
              <Download aria-hidden="true" className="h-4 w-4" />
              Download the App
            </Link>
          </nav>
        </div>
      </header>
    </>
  );
};
